// Small generic yes/no confirmation modal, used for the destructive actions in
// bulk add (cancelling a session, confirming a submit). Matches the app's
// existing modal pattern (native <dialog>, click-outside + Escape to dismiss).
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface BulkConfirmDialogProps {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Generic confirm dialog for bulk-add actions. */
export function BulkConfirmDialog({
  title,
  message,
  confirmLabel,
  danger = false,
  onConfirm,
  onClose,
}: BulkConfirmDialogProps) {
  const dialog = (
    <dialog
      open
      className="bulk-overlay"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="bulk-dialog bulk-dialog--confirm">
        <h2 className="bulk-dialog__title">{title}</h2>
        <p className="bulk-dialog__lead">{message}</p>
        <div className="bulk-dialog__actions">
          <button type="button" className="bulk-btn bulk-btn--ghost" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className={`bulk-btn ${danger ? 'bulk-btn--danger' : 'bulk-btn--primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );

  return createPortal(dialog, document.body);
}
